import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/client';
import type { EvolutionPrediction } from '../../api/client';
import ProvenanceBadge from './ProvenanceBadge';
import { formatEvoValue } from './format';

export default function ForecastTripwirePanel({ engineId = 3 }: { engineId?: number }) {
  const q = useQuery({ queryKey: ['evolution', 'predictions'], queryFn: api.evolutionPredictions });
  if (q.isLoading) return <div className="panel"><div className="small faint">Loading projected state...</div></div>;
  if (q.isError) return <div className="panel"><div className="small" style={{ color: 'var(--red)' }}>Projected state unavailable.</div></div>;

  const prediction = (q.data ?? []).find((item) => item.engine_ref.engine_id === engineId);
  if (!prediction) {
    return (
      <div className="panel" style={{ borderLeft: '3px solid var(--olive)' }}>
        <div className="qhead"><span className="qdot" style={{ background: 'var(--olive)' }} />Projected state</div>
        <div className="small" style={{ color: 'var(--muted)' }}>No projected breaches in horizon.</div>
      </div>
    );
  }
  return <ForecastCard prediction={prediction} />;
}

function ForecastCard({ prediction }: { prediction: EvolutionPrediction }) {
  const firstProjected = Object.values(prediction.projected_state).find(
    (value): value is import('../../types').EvoValue => typeof value === 'object' && value !== null && 'provenance' in value,
  );
  return (
    <div className="panel" style={{ borderLeft: `3px solid ${prediction.projected_tripwire.breached ? 'var(--red)' : 'var(--olive)'}` }}>
      <div className="row" style={{ justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
        <div>
          <div className="qhead"><span className="qdot" style={{ background: prediction.projected_tripwire.breached ? 'var(--red)' : 'var(--olive)' }} />Projected state</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>
            {prediction.projected_tripwire.breached ? 'Projected breach' : 'Within horizon band'}
            {firstProjected && <ProvenanceBadge value={firstProjected} />}
          </div>
          <div className="small" style={{ marginTop: 5, color: 'var(--muted)' }}>
            {prediction.precursor_evidence[0]} · {prediction.model_ref.validation_status}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span className={`sev-chip ${prediction.projected_tripwire.breached ? 'high' : 'watch'}`}>
            {prediction.projected_tripwire.breached ? 'BREACH' : 'CLEAR'}
          </span>
          <div className="small faint" style={{ marginTop: 6 }}>{prediction.model_ref.model_id}</div>
        </div>
      </div>
      <div className="grid2" style={{ marginTop: 12 }}>
        <Field label="Rule" value={prediction.projected_tripwire.rule_id} />
        <Field label="Crossing" value={prediction.projected_tripwire.projected_crossing_date ?? 'No crossing in horizon'} />
        <Field label="Threshold" value={formatEvoValue(prediction.projected_tripwire.threshold)} />
        <Field label="Owner" value={prediction.recommended_action.owner} />
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="field">
      <div className="field-label">{label}</div>
      <div className="field-value">{value}</div>
    </div>
  );
}
